/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { ATTENDANCE_COLUMNS } from './attendance-columns';

/**
 * The filters the Atendimentos header offers.
 *
 * Every field is a *server-side* query parameter on the per-column deal
 * queries — none of them is a predicate applied to already-fetched pages.
 * That distinction is the whole point: filtering in the browser only ever
 * searched the rows that happened to be in the current page, which is the
 * bug the per-column server queries removed.
 */
export interface AttendanceFilters {
  /** `origin_channel` — a single capture channel, or empty for all. */
  originChannel?: string;
  /** `stages` — the stages to keep; empty means every board stage. */
  stages?: readonly string[];
  /** `broker_id` — the responsible agency member. See {@link BROKER_FILTER_ENABLED}. */
  brokerId?: string;
}

/**
 * The stages a user may filter by: exactly the ones the board shows.
 *
 * Derived from the columns rather than from `DEAL_STAGES` so the two can
 * never disagree. `paused` is absent for the same reason it has no column
 * — a paused attendance is off the board, so offering it here would only
 * ever empty every column.
 */
export const FILTERABLE_STAGES = ATTENDANCE_COLUMNS.flatMap(
  (column) => [...column.stages],
) as readonly string[];

/**
 * Whether the corretor filter is offered.
 *
 * Enabled once the deal list endpoint accepts `broker_id` as a query
 * parameter (POS_ALUGUEL_FIX_PLAN follow-up, commit 3e506da79) — before
 * that, sending it was silently dropped and the board would answer as if
 * no corretor had been picked, so the control stayed hidden rather than
 * lying.
 */
export const BROKER_FILTER_ENABLED = true;

/**
 * Narrow a column's stages by the user's stage selection.
 *
 * @param columnStages - The stages the column covers.
 * @param selected - The user's stage selection; empty means no narrowing.
 * @returns The stages to actually ask the server for — empty when the
 *   selection excludes the column entirely, which hides it.
 */
export function stagesForColumn(
  columnStages: readonly string[],
  selected: readonly string[] = [],
): readonly string[] {
  if (selected.length === 0) return columnStages;
  return columnStages.filter((stage) => selected.includes(stage));
}
